import api from './api'

export interface Project {
  id: number
  user_id: number
  name: string
  description: string | null
  product_info: string | null
  tone_guidelines: string | null
  target_audience: string | null
  seed_templates: string | null
  warmup_prompt: string | null
  seed_prompt: string | null
  created_at: string
  updated_at: string | null
  channels_count: number
  leads_count: number
}

export interface ProjectCreate {
  name: string
  description?: string
  product_info?: string
  tone_guidelines?: string
  target_audience?: string
  seed_templates?: string
  warmup_prompt?: string
  seed_prompt?: string
}

export interface ProjectChannel {
  id: number
  project_id: number
  channel_id: number
  channel_title: string
  channel_username: string | null
  channel_type: string | null
  members_count: number | null
  status: string
  warmup_count: number
  warmup_target: number
  last_seeded_at: string | null
  cooldown_until: string | null
  notes: string | null
  added_at: string
  seeding_score: number | null
  recommendation: string | null
}

export interface ChannelAssessment {
  id: number
  project_channel_id: number
  tone_profile: string | null
  detected_rules: string | null
  topics: string | null
  language: string | null
  activity_score: number | null
  audience_quality: number | null
  seeding_score: number | null
  recommendation: string | null
  ai_summary: string | null
  messages_analyzed: number
  status: string
  assessed_at: string | null
  reviewed_at: string | null
}

export interface SeedPost {
  id: number
  project_channel_id: number
  post_type: string
  content: string
  edited_content: string | null
  reply_to_message_id: number | null
  reply_to_text: string | null
  status: string
  sent_at: string | null
  telegram_message_id: number | null
  reactions_count: number
  replies_count: number
  created_at: string
}

export interface AvailableChannel {
  id: number
  telegram_id: number
  title: string
  username: string | null
  channel_type: string | null
  members_count: number | null
  description: string | null
}

export interface ProjectFunnelStats {
  total_channels: number
  added: number
  assessing: number
  assessed: number
  warming_up: number
  warm: number
  seeded: number
  cooldown: number
  total_leads: number
  total_seed_posts: number
  sent_seed_posts: number
}

export const projectsService = {
  // Projects
  async getProjects(): Promise<Project[]> {
    return (await api.get<Project[]>('/projects')).data
  },
  async getProject(id: number): Promise<Project> {
    return (await api.get<Project>(`/projects/${id}`)).data
  },
  async createProject(data: ProjectCreate): Promise<Project> {
    return (await api.post<Project>('/projects', data)).data
  },
  async updateProject(id: number, data: Partial<ProjectCreate>): Promise<Project> {
    return (await api.patch<Project>(`/projects/${id}`, data)).data
  },
  async deleteProject(id: number): Promise<void> {
    await api.delete(`/projects/${id}`)
  },

  // Channels in project
  async getProjectChannels(projectId: number): Promise<ProjectChannel[]> {
    return (await api.get<ProjectChannel[]>(`/projects/${projectId}/channels`)).data
  },
  async addChannel(projectId: number, data: { channel_id?: number; username?: string; notes?: string }): Promise<ProjectChannel> {
    return (await api.post<ProjectChannel>(`/projects/${projectId}/channels`, data)).data
  },
  async bulkAddChannels(projectId: number, handles: string[]): Promise<{ added: number; not_found: string[] }> {
    return (await api.post(`/projects/${projectId}/channels/bulk`, { handles })).data
  },
  async getAvailableChannels(projectId: number): Promise<AvailableChannel[]> {
    return (await api.get<AvailableChannel[]>(`/projects/${projectId}/channels/available`)).data
  },
  async bulkAddByIds(projectId: number, channelIds: number[]): Promise<{ added: number; skipped: number[] }> {
    return (await api.post(`/projects/${projectId}/channels/bulk-select`, channelIds)).data
  },
  async updateChannelStatus(projectId: number, pcId: number, data: { status?: string; notes?: string }): Promise<ProjectChannel> {
    return (await api.patch<ProjectChannel>(`/projects/${projectId}/channels/${pcId}`, data)).data
  },
  async removeChannel(projectId: number, pcId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/channels/${pcId}`)
  },

  // Assessment
  async getAssessment(projectId: number, pcId: number): Promise<ChannelAssessment> {
    return (await api.get<ChannelAssessment>(`/projects/${projectId}/channels/${pcId}/assessment`)).data
  },
  async triggerAssessment(projectId: number, pcId: number): Promise<void> {
    await api.post(`/projects/${projectId}/channels/${pcId}/assess`)
  },
  async approveAssessment(projectId: number, pcId: number): Promise<void> {
    await api.post(`/projects/${projectId}/channels/${pcId}/assessment/approve`)
  },
  async rejectAssessment(projectId: number, pcId: number): Promise<void> {
    await api.post(`/projects/${projectId}/channels/${pcId}/assessment/reject`)
  },

  // Seed posts
  async getSeedPosts(projectId: number, params?: { pc_id?: number; status?: string; post_type?: string }): Promise<SeedPost[]> {
    return (await api.get<SeedPost[]>(`/projects/${projectId}/posts`, { params })).data
  },
  async generatePost(projectId: number, pcId: number): Promise<void> {
    await api.post(`/projects/${projectId}/channels/${pcId}/generate-post`)
  },
  async createPost(projectId: number, pcId: number, data: { content: string; post_type?: string }): Promise<SeedPost> {
    return (await api.post<SeedPost>(`/projects/${projectId}/channels/${pcId}/posts`, data)).data
  },
  async updatePost(projectId: number, postId: number, data: { edited_content?: string; status?: string }): Promise<SeedPost> {
    return (await api.patch<SeedPost>(`/projects/${projectId}/posts/${postId}`, data)).data
  },
  async sendPost(projectId: number, postId: number): Promise<void> {
    await api.post(`/projects/${projectId}/posts/${postId}/send`)
  },

  // Stats
  async getStats(projectId: number): Promise<ProjectFunnelStats> {
    return (await api.get<ProjectFunnelStats>(`/projects/${projectId}/stats`)).data
  },
}
